/**
 * The directory every sandbox mount root sits directly under.
 *
 * Declared because a RUNTIME ASSERTION needs a target, not because anything
 * builds a path from it. `buildMountCommand` refuses to mount unless this
 * directory is owned by root with no group or other write bit — see that
 * method for why the invariant matters and what it does not claim to close.
 *
 * It is the COMMON ANCESTOR of every sandbox mount root, and that is a
 * property rather than an intention:
 * `apps/backend/src/external/daytona/__tests__/mount-roots-share-one-parent.spec.ts`
 * asserts it in both directions, so a root added under a different parent takes
 * that spec red instead of silently escaping the assertion.
 *
 * 🚨 ONLY TWO OF THOSE ROOTS ARE IN THIS FILE. The third is
 * `SANDBOX_MOUNT_ROOT = "/mnt/workspaces"` — the code-interpreter grammar,
 * PLURAL — and it lives in the backend at
 * `apps/backend/src/chats/code-interpreter/sandbox-mount-key.ts`, importable
 * from here by nobody. So "the mount roots are in this file" is wrong by one,
 * which is exactly why the spec imports from both packages and why the
 * assertion targets ONE ancestor rather than a per-root string a new grammar
 * can be added beside. `CUE_SHARED_WORKSPACE_SIBLING_ROOT` below records what
 * that class of near-collision has already cost once.
 */
export const SANDBOX_MOUNT_PARENT_DIR = "/mnt";

/**
 * Sandbox-only path segment for admin shared workspaces.
 * Workspace slugs are alphanumeric + hyphen only, so the underscore keeps
 * this namespace reserved from user-created workspace slugs.
 */
export const ADMIN_SHARED_SANDBOX_MOUNT_SEGMENT = "_shared";

/**
 * Where an organization's own workspace storage is mounted inside a sandbox.
 *
 * The name is historical: this was the shared Daytona volume's mount path. The
 * volume is gone (NEX-2764) — the path survives because mount-s3 now mounts the
 * org's BUCKET ROOT here, so it is the string agents see and the string the
 * prompt teaches them.
 *
 * It lives in this package, not in the backend, because the FRONTEND has to
 * spell it too: the workspace indicator matches a conversation's recorded boot
 * mount paths against it. When it lived backend-only the indicator hard-coded
 * its own copy, and one of the two copies was wrong for four months — see
 * `CUE_SHARED_WORKSPACE_SIBLING_ROOT` below for which.
 */
export const CUE_ORG_SANDBOX_MOUNT_ROOT = "/mnt/workspace";

/**
 * Root for Cue admin-shared sandbox mounts. A SIBLING of
 * `CUE_ORG_SANDBOX_MOUNT_ROOT`, not nested under it: the kernel refuses to
 * attach any mount at a path that resolves through a mountpoint-s3 filesystem,
 * so the volume-era `/mnt/workspace/_shared/<slug>` layout is impossible with
 * in-sandbox mount-s3.
 *
 * THE BUG THIS CONSTANT'S LOCATION EXISTS TO PREVENT (found in dev,
 * 2026-08-20): the backend moved shared mounts to this sibling root while the
 * frontend indicator kept building the nested
 * `<org root>/${ADMIN_SHARED_SANDBOX_MOUNT_SEGMENT}/<slug>/` path. The two
 * could never match, so the indicator dropped EVERY shared workspace for every
 * organization — visible before the first message (when it does not filter) and
 * gone the moment a conversation existed. `_shared` is the CODE-INTERPRETER
 * grammar, where shared workspaces really are children of the mount root; a
 * hook serving both surfaces reached for the wrong one because the right one
 * was not importable from the frontend at all.
 */
export const CUE_SHARED_WORKSPACE_SIBLING_ROOT = "/mnt/workspace-shared";
